import type {
  AllowReflashResponse,
  BootMediaRotationPreview,
  BootMediaRotationResult,
  Channel,
  ScheduleEntry,
  Source,
  SourceType,
  SourceConfig,
  Ad,
  AdSegment,
  AdVariant,
  AdPod,
  AnalysisStatus,
  Site,
  SitePlatformVersions,
  Machine,
  HelmRelease,
  ClusterHealth,
  ChartVersion,
  SitePod,
  SiteProductions,
  ClusterNode,
  ClusterPod,
  ClusterHealthLive,
  ClusterEvent,
  PodDetail,
  ServiceInfo,
  EndpointInfo,
  PVCInfo,
  ClusterStorage,
  PodMetrics,
  K8sDeployment,
  DeploymentEditableSpec,
  DeploymentEditablePatch,
  Workload,
  WorkloadPod,
  AnalyticsOverview,
  ChannelMetrics,
  SessionSummary,
  QualityBucket,
  RetentionPoint,
  HeatmapCell,
  PostingEngagement,
  SessionTimelinePoint,
  Insight,
  SocialAccount,
  SocialPost,
  LinkAccountRequest,
  CreatePostRequest,
  ContentItem,
  EPGGuide,
  ChannelType,
  FastChannelConfig,
  VastProvider,
  AdTrackingRecord,
  TrackingStats,
  VastTestResult,
  ManifestState,
  Organization,
  OrgMember,
  MyOrganization,
  CreateOrgRequest,
  UpdateOrgRequest,
  AdminOnboardRequest,
  AdminOnboardResponse,
  DrmStats,
  DrmKey,
  DrmSession,
  DrmDevice,
  JeketEvent,
  PublicEventView,
  ChatMessage,
  QAQuestion,
  ChatRoom,
  HelmReleaseHistory,
  EventParticipant,
  ParticipantSlotsView,
  TalosServiceInfo,
  TalosDmesgResponse,
  KnownErrorPattern,
  DrainPreview,
  SiteReadiness,
  PrerequisiteStatus,
  SiteOperatorStatus,
} from '@/types';
import type {
  FeatureFlagValues,
  NodePoolConformance,
  NodePoolLayout,
  NodePoolProposal,
  NodePoolRescheduleResult,
  NodePoolStage,
  NodePoolStagePreview,
  NodePoolValidation,
  NodePoolsView,
  OrgEntitlements,
} from '@jeket-cloud/types';
import { ApiError } from '@/lib/apiError';
import type {
  ApproveClipRequest,
  ApproveClipResponse,
  Clip,
  CreateJobRequest,
  JobList,
  JobListParams,
  ProcessingJob,
  ProcessingResult,
  ProcessingSettings,
  Recording,
  RecordingDetail,
  ResultKind,
  TamsFlow,
  TamsSegment,
} from '@/types/processing';

// ---- Helpers ----

export function getAccessToken(): string | null {
  // Read token from oidc-client-ts sessionStorage
  // The key format is: oidc.user:{authority}:{client_id}
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith('oidc.user:')) {
      try {
        const data = JSON.parse(sessionStorage.getItem(key)!);
        return data.access_token ?? null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

// Org override for admin impersonation (persisted in localStorage)
const ORG_OVERRIDE_KEY = 'jeket_org_override';
let _orgOverride: string | null = localStorage.getItem(ORG_OVERRIDE_KEY);
export function setOrgOverride(orgId: string | null) {
  _orgOverride = orgId;
  if (orgId) localStorage.setItem(ORG_OVERRIDE_KEY, orgId);
  else localStorage.removeItem(ORG_OVERRIDE_KEY);
}
export function getOrgOverride(): string | null { return _orgOverride; }

const ANONYMOUS_SESSION_KEY = 'jeket.chat.anonymousSession';
let _anonymousSession: string | null = null;

/**
 * This tab's anonymous chat/Q&A session token (JSS#1191): random, made once
 * per tab and kept in sessionStorage. social-service derives a signed-out
 * viewer's anonymous author id from it by hashing, so chat and Q&A see one
 * stable anonymous author and an upvote counts once. It is no identity
 * claim: it only lets this tab be the same anonymous viewer again.
 */
export function getAnonymousSession(): string {
  if (_anonymousSession) return _anonymousSession;
  try {
    const stored = sessionStorage.getItem(ANONYMOUS_SESSION_KEY);
    if (stored && /^[A-Za-z0-9_-]{22,128}$/.test(stored)) return (_anonymousSession = stored);
  } catch {
    // Storage blocked: an in-memory session for this page load.
  }
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  _anonymousSession = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  try {
    sessionStorage.setItem(ANONYMOUS_SESSION_KEY, _anonymousSession);
  } catch {
    // Kept in memory only.
  }
  return _anonymousSession;
}

/**
 * Headers for the public chat and Q&A calls: always the tab's anonymous
 * session. social-service uses it only when the caller is anonymous (a
 * signed-out viewer, or a sign-in the BFF could not verify), where an
 * anonymous write without one is refused (JSS#1191).
 */
function chatQaHeaders(): Record<string, string> {
  return { 'X-Anonymous-Session': getAnonymousSession() };
}

/**
 * The signed-in user's credentials for a BFF call: the bearer, read fresh on
 * every call so a silently renewed token is used, and the org-switcher pick.
 * authenticatedRequest and Shaka's request filter (lib/shakaApiAuth.ts) both
 * send exactly these. `actingOrgId` replaces the switcher's pick for one call.
 */
export function apiAuthHeaders(actingOrgId: string | null = _orgOverride): Record<string, string> {
  const token = getAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(actingOrgId ? { 'X-Org-Override': actingOrgId } : {}),
  };
}

interface RequestOptions extends RequestInit {
  /**
   * Act in this org for this one call instead of the org-switcher pick: a
   * superuser's explicit owner org on a create (JSS#959). The BFF takes it
   * as it takes the switcher's pick, so the service sees the same org in
   * X-User-Org as in the body's `orgId`, never an org_mismatch.
   */
  actingOrgId?: string;
}

async function authenticatedRequest<T>(url: string, options?: RequestOptions): Promise<T> {
  const { headers, actingOrgId, ...rest } = options ?? {};
  const res = await fetch(url, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...(headers as Record<string, string>),
      ...apiAuthHeaders(actingOrgId),
    },
  });

  if (!res.ok) {
    // ApiError keeps the "API error <status>: <body>" message and adds the
    // status + parsed body, so callers can show a 409's reason (#804).
    throw new ApiError(res.status, await res.text());
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// ---- Channels API ----

export const channelsApi = {
  list(): Promise<Channel[]> {
    return authenticatedRequest('/api/channels');
  },

  get(id: string): Promise<Channel> {
    return authenticatedRequest(`/api/channels/${id}`);
  },

  create(data: {
    name: string;
    description?: string;
    config?: Partial<Channel['config']>;
    channel_type?: ChannelType;
    fast_config?: FastChannelConfig;
  }): Promise<Channel> {
    return authenticatedRequest('/api/channels', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: string, data: Partial<Channel>): Promise<Channel> {
    return authenticatedRequest(`/api/channels/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  start(id: string): Promise<Channel> {
    return authenticatedRequest(`/api/channels/${id}/start`, {
      method: 'POST',
    });
  },

  stop(id: string): Promise<Channel> {
    return authenticatedRequest(`/api/channels/${id}/stop`, {
      method: 'POST',
    });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/channels/${id}`, {
      method: 'DELETE',
    });
  },

  getSchedule(channelId: string): Promise<ScheduleEntry[]> {
    return authenticatedRequest(`/api/channels/${channelId}/schedule`);
  },

  addScheduleEntry(
    channelId: string,
    data: {
      title: string;
      sourceType: string;
      sourceRef?: string;
      startTime: string;
      durationMs: number;
    }
  ): Promise<ScheduleEntry> {
    return authenticatedRequest(`/api/channels/${channelId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateScheduleEntry(
    channelId: string,
    entryId: string,
    data: Partial<ScheduleEntry>
  ): Promise<ScheduleEntry> {
    return authenticatedRequest(`/api/channels/${channelId}/schedule/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteScheduleEntry(channelId: string, entryId: string): Promise<void> {
    return authenticatedRequest(`/api/channels/${channelId}/schedule/${entryId}`, {
      method: 'DELETE',
    });
  },

  triggerFastFill(channelId: string): Promise<{ success: boolean; entriesAdded: number }> {
    return authenticatedRequest(`/api/channels/${channelId}/fast/fill`, {
      method: 'POST',
    });
  },
};

// ---- Sources API ----

export const sourcesApi = {
  list(): Promise<Source[]> {
    return authenticatedRequest('/api/sources');
  },

  get(id: string): Promise<Source> {
    return authenticatedRequest(`/api/sources/${id}`);
  },

  create(data: {
    name: string;
    description?: string;
    type: SourceType;
    config?: Partial<SourceConfig>;
  }): Promise<Source> {
    return authenticatedRequest('/api/sources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(
    id: string,
    data: Partial<{ name: string; description: string; config: SourceConfig }>
  ): Promise<Source> {
    return authenticatedRequest(`/api/sources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/sources/${id}`, {
      method: 'DELETE',
    });
  },
};

// ---- Segments API ----

export const segmentsApi = {
  getState(sourceId: string): Promise<ManifestState> {
    return authenticatedRequest(`/api/segments/${sourceId}`);
  },
};

// ---- Ads API ----

export const adsApi = {
  list(): Promise<Ad[]> {
    return authenticatedRequest('/api/ads');
  },

  get(id: number): Promise<Ad> {
    return authenticatedRequest(`/api/ads/${id}`);
  },

  upload(file: File, metadata?: { name?: string; advertiser?: string }): Promise<Ad> {
    const formData = new FormData();
    formData.append('file', file);
    if (metadata?.name) formData.append('name', metadata.name);
    if (metadata?.advertiser) formData.append('advertiser', metadata.advertiser);

    const token = getAccessToken();
    return fetch('/api/ads/upload', {
      method: 'POST',
      body: formData,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then((res) => {
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
      return res.json();
    });
  },

  update(id: number, data: Partial<Ad>): Promise<Ad> {
    return authenticatedRequest(`/api/ads/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: number): Promise<void> {
    return authenticatedRequest(`/api/ads/${id}`, {
      method: 'DELETE',
    });
  },

  analyze(id: number): Promise<{ message: string }> {
    return authenticatedRequest(`/api/ads/${id}/analyze`, {
      method: 'POST',
    });
  },

  getAnalysisStatus(id: number): Promise<AnalysisStatus> {
    return authenticatedRequest(`/api/ads/${id}/analysis-status`);
  },

  generateVariants(
    id: number,
    targetDurations: number[]
  ): Promise<{ message: string; variants: AdVariant[] }> {
    return authenticatedRequest(`/api/ads/${id}/generate-variants`, {
      method: 'POST',
      body: JSON.stringify({ targetDurations }),
    });
  },

  getVariants(adId: number): Promise<AdVariant[]> {
    return authenticatedRequest(`/api/ads/${adId}/variants`);
  },

  getSegments(adId: number): Promise<AdSegment[]> {
    return authenticatedRequest(`/api/ads/${adId}/segments`);
  },
};

// ---- Ad Pods API ----

export const adPodsApi = {
  list(): Promise<AdPod[]> {
    return authenticatedRequest('/api/adpods');
  },

  get(id: number): Promise<AdPod> {
    return authenticatedRequest(`/api/adpods/${id}`);
  },

  create(data: {
    name?: string;
    channelId?: string;
    durationMs: number;
    position?: string;
    triggerType?: string;
  }): Promise<AdPod> {
    return authenticatedRequest('/api/adpods', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: number, data: Partial<AdPod>): Promise<AdPod> {
    return authenticatedRequest(`/api/adpods/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  fill(id: number): Promise<AdPod> {
    return authenticatedRequest(`/api/adpods/${id}/fill`, {
      method: 'POST',
    });
  },

  delete(id: number): Promise<void> {
    return authenticatedRequest(`/api/adpods/${id}`, {
      method: 'DELETE',
    });
  },
};

// ---- VAST Providers API ----

export const vastProvidersApi = {
  list(): Promise<VastProvider[]> {
    return authenticatedRequest('/api/vast-providers');
  },

  get(id: string): Promise<VastProvider> {
    return authenticatedRequest(`/api/vast-providers/${id}`);
  },

  create(data: {
    name: string;
    providerType: string;
    vastTagUrl: string;
    priority?: number;
    config?: Partial<VastProvider['config']>;
  }): Promise<VastProvider> {
    return authenticatedRequest('/api/vast-providers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: string, data: Partial<{
    name: string;
    providerType: string;
    vastTagUrl: string;
    status: string;
    priority: number;
    config: Partial<VastProvider['config']>;
  }>): Promise<VastProvider> {
    return authenticatedRequest(`/api/vast-providers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/vast-providers/${id}`, {
      method: 'DELETE',
    });
  },

  test(id: string): Promise<VastTestResult> {
    return authenticatedRequest(`/api/vast-providers/${id}/test`, {
      method: 'POST',
    });
  },
};

// ---- Tracking API ----

export const trackingApi = {
  fireEvent(
    channelId: string,
    breakId: string,
    data: { creativeId: string; event: string }
  ): Promise<{ message: string }> {
    return authenticatedRequest(`/api/tracking/${channelId}/${breakId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getHistory(channelId: string): Promise<AdTrackingRecord[]> {
    return authenticatedRequest(`/api/tracking/${channelId}`);
  },

  getStats(): Promise<TrackingStats> {
    return authenticatedRequest('/api/tracking/stats');
  },
};

// ---- Auth API ----

export const authApi = {
  me(): Promise<{ success: boolean; user: { id: string; username: string; email?: string; name?: string; role: string; orgId?: string } }> {
    return authenticatedRequest('/api/auth/me');
  },

  getProfile(): Promise<{ success: boolean; user: { id: string; username: string; email?: string; firstName?: string; lastName?: string } }> {
    return authenticatedRequest('/api/auth/profile');
  },

  updateProfile(data: { firstName?: string; lastName?: string }): Promise<{ success: boolean; user: { id: string; username: string; email?: string; firstName?: string; lastName?: string } }> {
    return authenticatedRequest('/api/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // No changePassword: the BFF never handles passwords (#998). A user changes
  // theirs in Keycloak's UPDATE_PASSWORD required action, which
  // AuthContext.changePassword starts.
};

// ---- Sites API ----

export const sitesApi = {
  list(): Promise<Site[]> {
    return authenticatedRequest('/api/sites');
  },

  get(id: string): Promise<Site> {
    return authenticatedRequest(`/api/sites/${id}`);
  },

  /** The Talos / Kubernetes versions a new site is created at: derived from
   *  the certified hardware profiles, never chosen by the customer. */
  platformVersions(): Promise<SitePlatformVersions> {
    return authenticatedRequest('/api/sites/platform-versions');
  },

  // No talosVersion / kubernetesVersion: deployment-service sets them from
  // the certified hardware profiles (and refuses uncertified ones).
  //
  // `orgId` is a superuser's explicit owner org (JSS#959); the call acts in
  // it. Without one the site is the acting org's: an org user's own, or the
  // switcher's pick.
  create(data: {
    name: string;
    description?: string;
    location: string;
    clusterName?: string;
    lockdown?: boolean;
    controlPlaneVip?: string;
    controlPlaneCount?: number;
    metallbIngestPool?: string;
    metallbGeneralPool?: string;
    config?: Partial<Site['config']>;
    orgId?: string;
  }): Promise<Site> {
    return authenticatedRequest('/api/sites', {
      method: 'POST',
      body: JSON.stringify(data),
      actingOrgId: data.orgId || undefined,
    });
  },

  update(id: string, data: Partial<Site>): Promise<Site> {
    return authenticatedRequest(`/api/sites/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  /** Lock a site down or lift it: platform superuser only, at the BFF and in
   *  deployment-service (JSS#993). PATCH refuses `lockdown`. */
  setLockdown(id: string, lockdown: boolean): Promise<{ id: string; lockdown: boolean }> {
    return authenticatedRequest(`/api/sites/${id}/lockdown`, {
      method: 'PUT',
      body: JSON.stringify({ lockdown }),
    });
  },

  activate(id: string): Promise<Site> {
    return authenticatedRequest(`/api/sites/${id}/activate`, { method: 'POST' });
  },

  decommission(id: string): Promise<Site> {
    return authenticatedRequest(`/api/sites/${id}/decommission`, { method: 'POST' });
  },

  /**
   * Re-run the site's prerequisites. Helm path: every step, answering the new
   * `prerequisites`. Operator path (#805): an immediate operator pass that
   * also re-seeds the site's credentials, answering its `operator` status, or
   * `queued: true` (202) when the site is busy and the next monitor tick runs it.
   */
  reprovision(id: string): Promise<{
    ok: boolean;
    prerequisites?: PrerequisiteStatus;
    operator?: SiteOperatorStatus | null;
    queued?: boolean;
  }> {
    return authenticatedRequest(`/api/sites/${id}/reprovision`, { method: 'POST' });
  },

  /** On-demand JBS install detection (#164) — re-checks the cluster
   * immediately rather than waiting for the next 60s monitor cycle. */
  checkJbsStatus(id: string): Promise<{ ok: boolean; status: string; message?: string }> {
    return authenticatedRequest(`/api/sites/${id}/check-jbs-status`, { method: 'POST' });
  },

  getHealth(id: string): Promise<ClusterHealth> {
    return authenticatedRequest(`/api/sites/${id}/health`);
  },

  /** "Ready for JBS" evaluation (#796). `refresh` re-runs every check now
   *  instead of returning the last persisted result (refreshed every ~5 min). */
  getReadiness(id: string, opts?: { refresh?: boolean }): Promise<SiteReadiness> {
    const qs = opts?.refresh ? '?refresh=true' : '';
    return authenticatedRequest(`/api/sites/${id}/readiness${qs}`);
  },

  getPods(id: string): Promise<SitePod[]> {
    return authenticatedRequest(`/api/sites/${id}/pods`);
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/sites/${id}`, { method: 'DELETE' });
  },

  async downloadFile(id: string, file: 'talosconfig' | 'kubeconfig'): Promise<Blob> {
    const token = getAccessToken();
    const res = await fetch(`/api/sites/${id}/${file}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(_orgOverride ? { 'X-Org-Override': _orgOverride } : {}),
      },
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`Download failed ${res.status}: ${body}`);
    }
    return res.blob();
  },

  /**
   * The site's USB install image, streamed through JSS (JSS#877). There is
   * deliberately no URL to hand out: the Image Factory link carries the
   * site's join token. The file name comes from the server (it names the site).
   */
  async downloadBootImage(id: string, arch: 'arm64' | 'amd64'): Promise<{ blob: Blob; filename: string }> {
    const token = getAccessToken();
    const res = await fetch(`/api/sites/${id}/boot-image?arch=${arch}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(_orgOverride ? { 'X-Org-Override': _orgOverride } : {}),
      },
    });
    if (!res.ok) {
      throw new ApiError(res.status, await res.text());
    }
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `metal-${arch}.iso`;
    return { blob: await res.blob(), filename };
  },

  /**
   * "Replace the USB image" (JSS#890), dry run: which boxes would lose their
   * connection to Jeket if the site's USB token were replaced now. Admin.
   */
  previewBootMediaRotation(id: string): Promise<BootMediaRotationPreview> {
    return authenticatedRequest(`/api/sites/${id}/boot-media/rotate`, {
      method: 'POST',
      body: JSON.stringify({ dryRun: true }),
    });
  },

  /**
   * Replace the site's USB image: JSS secures the installed boxes first,
   * then the old USB stops working for new boxes. Throws an ApiError 409
   * whose body is a `BootMediaStrandedResponse` while any box at risk is not
   * in `acknowledgedMachineIds`.
   */
  rotateBootMedia(id: string, acknowledgedMachineIds: string[]): Promise<BootMediaRotationResult> {
    return authenticatedRequest(`/api/sites/${id}/boot-media/rotate`, {
      method: 'POST',
      body: JSON.stringify({ acknowledgedMachineIds }),
    });
  },
};

// ---- Machines API ----

export const machinesApi = {
  list(siteId?: string): Promise<Machine[]> {
    const qs = siteId ? `?siteId=${siteId}` : '';
    return authenticatedRequest(`/api/machines${qs}`);
  },

  get(id: string): Promise<Machine> {
    return authenticatedRequest(`/api/machines/${id}`);
  },

  // No `register`: a hand-registered row has no SideroLink node UUID, so the
  // box's own enrollment never matches it and creates a second row (#804).
  // Boxes join a site by booting its install image (or netboot, where the
  // `netboot` feature flag is on, #954).
  //
  // No `assign` / `unassign` / role `update` either (JSS#874): a box belongs
  // to the site whose image it started from, and JSS gives it its role as it
  // joins. deployment-service refuses them; staff override a role from the
  // staff console.

  heartbeat(id: string): Promise<{ ok: boolean }> {
    return authenticatedRequest(`/api/machines/${id}/heartbeat`, { method: 'POST' });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/machines/${id}`, { method: 'DELETE' });
  },

  reinstall(id: string): Promise<Machine> {
    return authenticatedRequest(`/api/machines/${id}/reinstall`, { method: 'POST' });
  },

  reset(id: string): Promise<Machine> {
    return authenticatedRequest(`/api/machines/${id}/reset`, { method: 'POST' });
  },

  talosReset(id: string): Promise<Machine> {
    return authenticatedRequest(`/api/machines/${id}/talos-reset`, { method: 'POST' });
  },

  /**
   * Let a box that proved its own identity rejoin as itself from the site's
   * USB image for `hours` (1–72; the server defaults to 24). Admin
   * (JSS#883 security review).
   */
  allowReflash(id: string, hours?: number): Promise<AllowReflashResponse> {
    return authenticatedRequest(`/api/machines/${id}/allow-reflash`, {
      method: 'POST',
      body: JSON.stringify(hours === undefined ? {} : { hours }),
    });
  },
};

// ---- Releases API ----

export const releasesApi = {
  list(siteId?: string): Promise<HelmRelease[]> {
    const qs = siteId ? `?siteId=${siteId}` : '';
    return authenticatedRequest(`/api/releases${qs}`);
  },

  get(id: string): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}`);
  },

  create(data: {
    siteId: string;
    releaseName: string;
    chartName: string;
    chartVersion?: string;
    namespace?: string;
    values?: Record<string, unknown>;
  }): Promise<HelmRelease> {
    return authenticatedRequest('/api/releases', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: string, data: Partial<HelmRelease>): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  install(id: string): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/install`, { method: 'POST' });
  },

  upgrade(id: string, data?: { chartVersion?: string }): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/upgrade`, {
      method: 'POST',
      body: JSON.stringify(data ?? {}),
    });
  },

  rollback(id: string, revision?: number): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ revision }),
    });
  },

  uninstall(id: string): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/uninstall`, { method: 'POST' });
  },

  retry(id: string): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/retry`, { method: 'POST' });
  },

  forceCleanup(id: string): Promise<HelmRelease> {
    return authenticatedRequest(`/api/releases/${id}/force-cleanup`, { method: 'POST' });
  },

  history(id: string): Promise<HelmReleaseHistory[]> {
    return authenticatedRequest(`/api/releases/${id}/history`);
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/releases/${id}`, { method: 'DELETE' });
  },
};

// ---- Charts API ----

export const chartsApi = {
  listVersions(chartName: string): Promise<ChartVersion[]> {
    return authenticatedRequest(`/api/charts/${chartName}/versions`);
  },
};

// ---- Playback sessions API (session-service, JSS#1015) ----
// The player's sessions used plain fetch with no bearer, so the BFF answered
// every call 401 and no session ever reached session-service. Through
// authenticatedRequest they carry the bearer and the org-switcher pick, from
// which the BFF sets X-User-Org (a session is scoped to its org, #1007).

export interface PlaybackSession {
  id: string;
  channelId: string;
  cohort: string;
  transport: string;
  startedAt: string;
}

/** The session's ad splice plan (route-writer, via Redis). */
export interface SessionRoute {
  session: string;
  splice_map: Array<{
    at_group: number;
    for_groups: number;
    namespace: string;
  }>;
  expires_at: string;
}

export const sessionsApi = {
  create(channelId: string, transport: 'http' | 'moq'): Promise<PlaybackSession> {
    return authenticatedRequest('/api/sessions', {
      method: 'POST',
      body: JSON.stringify({ channelId, transport }),
    });
  },

  heartbeat(id: string): Promise<{ status: string }> {
    return authenticatedRequest(`/api/sessions/${encodeURIComponent(id)}/heartbeat`, { method: 'POST' });
  },

  /** 404 while the session has no route (no ad splice planned). */
  route(id: string): Promise<SessionRoute> {
    return authenticatedRequest(`/api/sessions/${encodeURIComponent(id)}/route`);
  },

  end(id: string): Promise<{ status: string }> {
    return authenticatedRequest(`/api/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};

// ---- Analytics API ----

export const analyticsApi = {
  getOverview(): Promise<AnalyticsOverview> {
    return authenticatedRequest('/api/analytics/overview');
  },

  getChannelMetrics(channelId: string, hours?: number): Promise<ChannelMetrics[]> {
    const qs = hours ? `?hours=${hours}` : '';
    return authenticatedRequest(`/api/analytics/channels/${channelId}${qs}`);
  },

  getQuality(channelId: string, hours?: number): Promise<QualityBucket[]> {
    const qs = hours ? `?hours=${hours}` : '';
    return authenticatedRequest(`/api/analytics/channels/${channelId}/quality${qs}`);
  },

  getSessions(channelId?: string): Promise<SessionSummary[]> {
    const qs = channelId ? `?channelId=${channelId}` : '';
    return authenticatedRequest(`/api/analytics/sessions${qs}`);
  },

  getRetention(channelId: string, buckets?: number, hours?: number): Promise<RetentionPoint[]> {
    const params = new URLSearchParams();
    if (buckets) params.set('buckets', String(buckets));
    if (hours) params.set('hours', String(hours));
    const qs = params.toString() ? `?${params}` : '';
    return authenticatedRequest(`/api/analytics/channels/${channelId}/retention${qs}`);
  },

  /**
   * Fetch the watch heat-map. Pattern-over-history view keyed on
   * (dayOfWeek, hourOfDay) — no "last N days" filter (see JSS#12).
   */
  getHeatmap(channelId?: string): Promise<HeatmapCell[]> {
    const params = new URLSearchParams();
    if (channelId) params.set('channelId', channelId);
    const qs = params.toString() ? `?${params}` : '';
    return authenticatedRequest(`/api/analytics/heatmap${qs}`);
  },

  getPostingEngagement(channelId?: string, days?: number): Promise<PostingEngagement[]> {
    const params = new URLSearchParams();
    if (channelId) params.set('channelId', channelId);
    if (days) params.set('days', String(days));
    const qs = params.toString() ? `?${params}` : '';
    return authenticatedRequest(`/api/analytics/posting-engagement${qs}`);
  },

  getSessionTimeline(sessionId: string): Promise<SessionTimelinePoint[]> {
    return authenticatedRequest(`/api/analytics/sessions/${sessionId}/timeline`);
  },

  getInsights(channelId?: string): Promise<Insight[]> {
    const qs = channelId ? `?channel_id=${channelId}` : '';
    return authenticatedRequest(`/api/analytics/insights${qs}`);
  },

  refreshInsights(): Promise<{ message: string }> {
    return authenticatedRequest('/api/analytics/insights/refresh', { method: 'POST' });
  },

  dismissInsight(id: number): Promise<void> {
    return authenticatedRequest(`/api/analytics/insights/${id}/dismiss`, { method: 'PATCH' });
  },
};

// ---- Social API ----

function toQuery(params?: Record<string, string | undefined>): string {
  if (!params) return '';
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null) qs.set(k, v);
  }
  const str = qs.toString();
  return str ? `?${str}` : '';
}

export const socialApi = {
  listAccounts(): Promise<SocialAccount[]> {
    return authenticatedRequest('/api/social/accounts');
  },

  linkAccount(data: LinkAccountRequest): Promise<SocialAccount> {
    return authenticatedRequest('/api/social/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  unlinkAccount(id: string): Promise<void> {
    return authenticatedRequest(`/api/social/accounts/${id}`, { method: 'DELETE' });
  },

  listPosts(params?: { status?: string; platform?: string }): Promise<SocialPost[]> {
    return authenticatedRequest(`/api/social/posts${toQuery(params)}`);
  },

  getPost(id: string): Promise<SocialPost> {
    return authenticatedRequest(`/api/social/posts/${id}`);
  },

  createPost(data: CreatePostRequest): Promise<SocialPost> {
    return authenticatedRequest('/api/social/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updatePost(id: string, data: Partial<CreatePostRequest>): Promise<SocialPost> {
    return authenticatedRequest(`/api/social/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deletePost(id: string): Promise<void> {
    return authenticatedRequest(`/api/social/posts/${id}`, { method: 'DELETE' });
  },

  publishPost(id: string): Promise<SocialPost> {
    return authenticatedRequest(`/api/social/posts/${id}/publish`, { method: 'POST' });
  },
};

// ---- Content API ----

export const contentApi = {
  list(params?: { category?: string; status?: string; search?: string }): Promise<ContentItem[]> {
    const qs = toQuery(params);
    return authenticatedRequest(`/api/content${qs}`);
  },

  get(id: string): Promise<ContentItem> {
    return authenticatedRequest(`/api/content/${id}`);
  },

  create(data: Partial<ContentItem>): Promise<ContentItem> {
    return authenticatedRequest('/api/content', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update(id: string, data: Partial<ContentItem>): Promise<ContentItem> {
    return authenticatedRequest(`/api/content/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/content/${id}`, { method: 'DELETE' });
  },

  getCategories(): Promise<string[]> {
    return authenticatedRequest('/api/content/categories');
  },
};

// ---- DRM API ----

// ---- EPG API ----

export const epgApi = {
  getGuide(channelId: string, hours?: number): Promise<EPGGuide> {
    const qs = hours ? `?hours=${hours}` : '';
    return authenticatedRequest(`/api/epg/${channelId}${qs}`);
  },
};

// ---- Admin API ----
// Platform org management — superuser-only at the BFF (#847).

export const adminApi = {
  onboard(data: AdminOnboardRequest): Promise<AdminOnboardResponse> {
    return authenticatedRequest('/api/admin/onboard', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  listOrganizations(): Promise<Organization[]> {
    return authenticatedRequest('/api/admin/organizations');
  },

  getOrganization(id: string): Promise<Organization> {
    return authenticatedRequest(`/api/admin/organizations/${id}`);
  },

  createOrganization(data: CreateOrgRequest): Promise<Organization> {
    return authenticatedRequest('/api/admin/organizations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateOrganization(id: string, data: UpdateOrgRequest): Promise<Organization> {
    return authenticatedRequest(`/api/admin/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  deleteOrganization(id: string): Promise<void> {
    return authenticatedRequest(`/api/admin/organizations/${id}`, {
      method: 'DELETE',
    });
  },

  listOrgMembers(orgId: string): Promise<OrgMember[]> {
    return authenticatedRequest(`/api/admin/organizations/${orgId}/members`);
  },
};

export const drmApi = {
  getStats(): Promise<DrmStats> {
    return authenticatedRequest('/api/drm/stats');
  },

  listKeys(contentId?: string): Promise<{ keys: DrmKey[] }> {
    const qs = contentId ? `?content_id=${contentId}` : '';
    return authenticatedRequest(`/api/drm/keys${qs}`);
  },

  listSessions(params?: { userId?: string; contentId?: string }): Promise<{ sessions: DrmSession[] }> {
    const qs = toQuery({ user_id: params?.userId, content_id: params?.contentId });
    return authenticatedRequest(`/api/drm/sessions${qs}`);
  },

  getSession(id: string): Promise<DrmSession> {
    return authenticatedRequest(`/api/drm/sessions/${id}`);
  },

  listDevices(userId?: string): Promise<{ devices: DrmDevice[] }> {
    const qs = userId ? `?user_id=${userId}` : '';
    return authenticatedRequest(`/api/drm/devices${qs}`);
  },
};

// ---- Events API ----

export const eventsApi = {
  list(params?: { status?: string; visibility?: string; upcoming?: string }): Promise<JeketEvent[]> {
    const qs = toQuery(params);
    return authenticatedRequest(`/api/events${qs}`);
  },

  get(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}`);
  },

  /**
   * The viewer page's event (#1175). Public: works signed out. Sends the
   * session's bearer and org pick when there is one, so the event's org gets
   * the whole event; anyone else gets the public projection of a public or
   * unlisted event, and 404 for a private one (same as an unknown slug).
   */
  getBySlug(slug: string): Promise<PublicEventView> {
    return authenticatedRequest(`/api/events/slug/${encodeURIComponent(slug)}`);
  },

  // `orgId` is a superuser's explicit owner org (JSS#959); the call acts in
  // it, as sitesApi.create does. Without one the event is the acting org's.
  create(data: {
    name: string;
    description?: string;
    visibility?: string;
    scheduledStart?: string;
    scheduledEnd?: string;
    config?: Partial<JeketEvent['config']>;
    tags?: string[];
    moqNamespace?: string | null;
    orgId?: string;
  }): Promise<JeketEvent> {
    return authenticatedRequest('/api/events', {
      method: 'POST',
      body: JSON.stringify(data),
      actingOrgId: data.orgId || undefined,
    });
  },

  update(
    id: string,
    // event-manager reads the schedule camelCase; `scheduledEnd: null`
    // clears the end (JSS#1152).
    data: Partial<JeketEvent> & {
      moqNamespace?: string | null;
      scheduledStart?: string | null;
      scheduledEnd?: string | null;
    },
  ): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  delete(id: string): Promise<void> {
    return authenticatedRequest(`/api/events/${id}`, { method: 'DELETE' });
  },

  schedule(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/schedule`, { method: 'POST' });
  },

  warmup(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/warmup`, { method: 'POST' });
  },

  goLive(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/go-live`, { method: 'POST' });
  },

  end(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/end`, { method: 'POST' });
  },

  archive(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/archive`, { method: 'POST' });
  },

  unschedule(id: string): Promise<JeketEvent> {
    return authenticatedRequest(`/api/events/${id}/unschedule`, { method: 'POST' });
  },
};

// ---- Chat API ----

export const chatApi = {
  getRoom(eventId: string): Promise<ChatRoom> {
    return authenticatedRequest(`/api/social/chat/rooms/${eventId}`, { headers: chatQaHeaders() });
  },

  getMessages(eventId: string, params?: { before?: string; limit?: number }): Promise<ChatMessage[]> {
    const qs = toQuery({
      before: params?.before,
      limit: params?.limit?.toString(),
    });
    return authenticatedRequest(`/api/social/chat/rooms/${eventId}/messages${qs}`, { headers: chatQaHeaders() });
  },

  /**
   * The server assigns the author (JSS#1191): the signed-in user, the
   * admitted guest, or an unverified anonymous viewer. `username` is only a
   * signed-out viewer's asked-for name, used where the event allows it.
   */
  sendMessage(eventId: string, data: { content: string; replyToId?: string; username?: string }): Promise<ChatMessage> {
    return authenticatedRequest(`/api/social/chat/rooms/${eventId}/messages`, {
      method: 'POST',
      headers: chatQaHeaders(),
      body: JSON.stringify(data),
    });
  },

  pinMessage(id: string, pinned: boolean): Promise<ChatMessage> {
    return authenticatedRequest(`/api/social/chat/messages/${id}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ pinned }),
    });
  },

  deleteMessage(id: string): Promise<void> {
    return authenticatedRequest(`/api/social/chat/messages/${id}`, { method: 'DELETE' });
  },

  clearAllMessages(eventId: string): Promise<{ cleared: number }> {
    return authenticatedRequest(`/api/social/chat/rooms/${eventId}/messages`, { method: 'DELETE' });
  },
};

// ---- Q&A API ----

export const qaApi = {
  list(eventId: string, status?: string): Promise<QAQuestion[]> {
    const qs = status ? `?status=${status}` : '';
    return authenticatedRequest(`/api/social/qa/${eventId}${qs}`, { headers: chatQaHeaders() });
  },

  /** The server assigns the asker (JSS#1191); `username` as for chatApi.sendMessage. */
  submit(eventId: string, data: { content: string; username?: string }): Promise<QAQuestion> {
    return authenticatedRequest(`/api/social/qa/${eventId}`, {
      method: 'POST',
      headers: chatQaHeaders(),
      body: JSON.stringify(data),
    });
  },

  approve(id: string): Promise<QAQuestion> {
    return authenticatedRequest(`/api/social/qa/${id}/approve`, { method: 'PATCH' });
  },

  /** The answerer is the signed-in moderator, set by the server (JSS#1191). */
  answer(id: string, data: { answer: string }): Promise<QAQuestion> {
    return authenticatedRequest(`/api/social/qa/${id}/answer`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  dismiss(id: string): Promise<QAQuestion> {
    return authenticatedRequest(`/api/social/qa/${id}/dismiss`, { method: 'PATCH' });
  },

  /** Counted once per server identity (JSS#1191): the user, the guest, or this tab's anonymous session. */
  upvote(id: string): Promise<QAQuestion> {
    return authenticatedRequest(`/api/social/qa/${id}/upvote`, { method: 'POST', headers: chatQaHeaders() });
  },
};

// ---- Participants API ----

export const participantsApi = {
  list(eventId: string, status?: string): Promise<EventParticipant[]> {
    const qs = status ? `?status=${status}` : '';
    return authenticatedRequest(`/api/events/${eventId}/participants${qs}`);
  },

  approve(eventId: string, pid: string): Promise<EventParticipant> {
    return authenticatedRequest(`/api/events/${eventId}/participants/${pid}/approve`, { method: 'PATCH' });
  },

  reject(eventId: string, pid: string): Promise<EventParticipant> {
    return authenticatedRequest(`/api/events/${eventId}/participants/${pid}/reject`, { method: 'PATCH' });
  },

  remove(eventId: string, pid: string): Promise<EventParticipant> {
    return authenticatedRequest(`/api/events/${eventId}/participants/${pid}`, { method: 'DELETE' });
  },
};

// ---- Participant slots (participant-loop contracts v2 §7, ADR-JSS-0012) ----
//
// The site declares N slots; the producer decides who is on each. A take is
// a route change at the participant gateway, never a pipeline build at the
// site, so nothing here calls JBS. Every route is the moderator's
// (PARTICIPANT_MODERATOR_ROLE), the read included. Each write answers 200
// with the view as GET returns it. Refusals: 400 a bad slot index or
// participantId, 404 an unknown event or participant, 409 `{ error,
// slotCount }` a slot beyond N, 409 a participant who is not admitted.

export const participantSlotsApi = {
  /** N, each slot's occupant, and every requested/admitted participant's presence. */
  get(eventId: string): Promise<ParticipantSlotsView> {
    return authenticatedRequest(`/api/events/${eventId}/participant-slots`);
  },

  /** Take: route the participant onto the slot, replacing any occupant (who stays admitted). */
  take(eventId: string, slot: number, participantId: string): Promise<ParticipantSlotsView> {
    return authenticatedRequest(`/api/events/${eventId}/participant-slots/${slot}`, {
      method: 'PUT',
      body: JSON.stringify({ participantId }),
    });
  },

  /** Clear the slot: it stays, black and silent at the site. */
  clear(eventId: string, slot: number): Promise<ParticipantSlotsView> {
    return authenticatedRequest(`/api/events/${eventId}/participant-slots/${slot}`, { method: 'DELETE' });
  },

  /** Conference: route admitted, connected participants onto free slots in admission order. */
  fill(eventId: string): Promise<ParticipantSlotsView> {
    return authenticatedRequest(`/api/events/${eventId}/participant-slots/fill`, { method: 'POST' });
  },
};

// ---- Site decommission pre-flight (#259) ----

export interface DecommissionPreviewIssue {
  reason: string;
  detail: string;
}

export interface DecommissionPreview {
  blockers: DecommissionPreviewIssue[];
  warnings: DecommissionPreviewIssue[];
  canDecommission: boolean;
}

// ---- Cluster API (live queries) ----

/**
 * A site's node pools (JSS#1104): the layout an Admin edits on the Node pools
 * page, the stages, conformance and Reschedule. Writes carry the revision the
 * page read; a 409 `revision_conflict` means someone else changed it.
 */
export const nodePoolsApi = {
  get(siteId: string): Promise<NodePoolsView> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools`);
  },

  propose(siteId: string): Promise<NodePoolProposal & { validation: NodePoolValidation }> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools/propose`, { method: 'POST' });
  },

  save(siteId: string, layout: NodePoolLayout, expectedRevision: number): Promise<{ revision: number; validation: NodePoolValidation }> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools`, {
      method: 'PUT',
      body: JSON.stringify({ ...layout, expectedRevision }),
    });
  },

  previewStage(siteId: string, stage: NodePoolStage): Promise<NodePoolStagePreview> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools/stage-preview${toQuery({ stage })}`);
  },

  setStage(siteId: string, stage: NodePoolStage, expectedRevision: number): Promise<{ revision: number; preview: NodePoolStagePreview }> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools/stage`, {
      method: 'POST',
      body: JSON.stringify({ stage, expectedRevision }),
    });
  },

  conformance(siteId: string): Promise<NodePoolConformance> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools/conformance`);
  },

  reschedule(siteId: string, items: { namespace: string; pod: string }[]): Promise<{ results: NodePoolRescheduleResult[] }> {
    return authenticatedRequest(`/api/cluster/${siteId}/node-pools/reschedule`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  },
};

export const clusterApi = {
  getNodes(siteId: string): Promise<ClusterNode[]> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes`);
  },

  /** The site's JBS productions (LiveEvents) and AirChains, read live (JSS#1161). */
  getProductions(siteId: string): Promise<SiteProductions> {
    return authenticatedRequest(`/api/cluster/${siteId}/productions`);
  },

  /**
   * Pre-flight check for the admin-tier decommission flow (#259). Lives
   * under clusterApi by convention — the endpoint physically sits on
   * /api/sites/ since the resource is the site itself.
   */
  getDecommissionPreview(siteId: string): Promise<DecommissionPreview> {
    return authenticatedRequest(`/api/sites/${siteId}/decommission-preview`);
  },

  getPods(siteId: string, filters?: { namespace?: string; node?: string; search?: string; phase?: string }): Promise<ClusterPod[]> {
    const qs = toQuery(filters);
    return authenticatedRequest(`/api/cluster/${siteId}/pods${qs}`);
  },

  getNamespaces(siteId: string): Promise<string[]> {
    return authenticatedRequest(`/api/cluster/${siteId}/namespaces`);
  },

  getHealth(siteId: string): Promise<ClusterHealthLive> {
    return authenticatedRequest(`/api/cluster/${siteId}/health`);
  },

  getEvents(siteId: string, filters?: { namespace?: string; type?: string; limit?: string }): Promise<ClusterEvent[]> {
    const qs = toQuery(filters);
    return authenticatedRequest(`/api/cluster/${siteId}/events${qs}`);
  },

  nodeAction(siteId: string, nodeIp: string, action: string, options?: Record<string, any>): Promise<{ success: boolean }> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, options }),
    });
  },

  cordonNode(siteId: string, nodeIp: string, cordon: boolean): Promise<{ success: boolean }> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/cordon`, {
      method: 'POST',
      body: JSON.stringify({ cordon }),
    });
  },

  drainNode(siteId: string, nodeIp: string): Promise<{ success: boolean }> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/drain`, {
      method: 'POST',
    });
  },

  /**
   * Read-only drain preview (#256). Lists pods that would be evicted by
   * `drainNode`, marks show-affecting workloads, and surfaces a critical
   * count so the UI can require type-the-name confirmation.
   */
  getDrainPreview(siteId: string, nodeIp: string): Promise<DrainPreview> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/drain-preview`);
  },

  /**
   * Replace a node's taints (#256). The server replaces the entire taints
   * array via strategic-merge patch.
   */
  patchNodeTaints(
    siteId: string,
    nodeIp: string,
    taints: { key: string; value?: string; effect: 'NoSchedule' | 'PreferNoSchedule' | 'NoExecute' }[],
  ): Promise<{ success: boolean; taintsApplied: number }> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/taints`, {
      method: 'PATCH',
      body: JSON.stringify({ taints }),
    });
  },

  /**
   * Engineer-tier read-only Talos diagnostics (#248).
   * The BFF gates these at `Role.Engineer`; viewers/operators get 403.
   */
  getNodeServices(siteId: string, nodeIp: string): Promise<TalosServiceInfo[]> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/services`);
  },

  getNodeDmesg(siteId: string, nodeIp: string, tail?: number): Promise<TalosDmesgResponse> {
    const qs = toQuery({ tail: tail?.toString() });
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/dmesg${qs}`);
  },

  /**
   * Admin-tier Talos service restart (#257). Wraps
   * `MachineService.ServiceRestart`. The BFF gates at Role.Admin; the
   * deployment-service mirror does the same. Use the existing `nodeAction`
   * helper for node-level reboot/shutdown/reset.
   */
  restartTalosService(siteId: string, nodeIp: string, serviceId: string): Promise<{ success: boolean }> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/nodes/${nodeIp}/services/${serviceId}/restart`,
      { method: 'POST' },
    );
  },

  /**
   * Admin-tier node reboot (#257). Wraps the existing /action route so
   * audit + role gating are shared with shutdown/reset/upgrade.
   */
  rebootNode(siteId: string, nodeIp: string): Promise<{ success: boolean }> {
    return authenticatedRequest(`/api/cluster/${siteId}/nodes/${nodeIp}/action`, {
      method: 'POST',
      body: JSON.stringify({ action: 'reboot' }),
    });
  },

  getPodDetail(siteId: string, namespace: string, podName: string): Promise<PodDetail> {
    return authenticatedRequest(`/api/cluster/${siteId}/pods/${namespace}/${podName}`);
  },

  getPodLogs(siteId: string, namespace: string, podName: string, params?: {
    container?: string; tailLines?: number; sinceSeconds?: number; previous?: boolean;
  }): Promise<string> {
    const qs = toQuery({
      container: params?.container,
      tailLines: params?.tailLines?.toString(),
      sinceSeconds: params?.sinceSeconds?.toString(),
      previous: params?.previous ? 'true' : undefined,
    });
    const token = getAccessToken();
    return fetch(`/api/cluster/${siteId}/pods/${namespace}/${podName}/logs${qs}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(_orgOverride ? { 'X-Org-Override': _orgOverride } : {}),
      },
    }).then((res) => {
      if (!res.ok) throw new Error(`API error ${res.status}`);
      return res.text();
    });
  },

  restartPod(siteId: string, namespace: string, podName: string): Promise<{ success: boolean; warning?: string }> {
    return authenticatedRequest(`/api/cluster/${siteId}/pods/${namespace}/${podName}/restart`, {
      method: 'POST',
    });
  },

  getServices(siteId: string, namespace?: string): Promise<ServiceInfo[]> {
    const qs = toQuery({ namespace });
    return authenticatedRequest(`/api/cluster/${siteId}/services${qs}`);
  },

  getServiceEndpoints(siteId: string, namespace: string, serviceName: string): Promise<EndpointInfo> {
    return authenticatedRequest(`/api/cluster/${siteId}/services/${namespace}/${serviceName}/endpoints`);
  },

  getStorage(siteId: string, params?: { namespace?: string; status?: string }): Promise<ClusterStorage> {
    const qs = toQuery(params);
    return authenticatedRequest(`/api/cluster/${siteId}/storage${qs}`);
  },

  getPodMetrics(siteId: string): Promise<PodMetrics[]> {
    return authenticatedRequest(`/api/cluster/${siteId}/pods/metrics`);
  },

  getDeployments(siteId: string, namespace?: string): Promise<K8sDeployment[]> {
    const qs = toQuery({ namespace });
    return authenticatedRequest(`/api/cluster/${siteId}/deployments${qs}`);
  },

  /**
   * Engineer-tier deployment editor (#250). Returns the editable subset of
   * a Deployment's spec (image / replicas / env / resources) plus a
   * read-only mirror of multi-container details and envFrom refs. The BFF
   * gates this at `Role.Engineer`; deployment-service mirrors via header.
   */
  getDeploymentSpec(siteId: string, namespace: string, name: string): Promise<DeploymentEditableSpec> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/deployments/${namespace}/${name}/spec`,
    );
  },

  /**
   * Patch a Deployment's editable subset (#250). Engineer+ only. Body is a
   * partial of the editable shape — fields omitted are left untouched.
   * env values are NEVER recorded in the audit row (they may be secrets);
   * a stable hash is recorded instead.
   */
  patchDeployment(
    siteId: string,
    namespace: string,
    name: string,
    body: DeploymentEditablePatch,
  ): Promise<{ ok: true; fieldsChanged: string[] }> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/deployments/${namespace}/${name}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  },

  getWorkloads(siteId: string): Promise<Workload[]> {
    return authenticatedRequest(`/api/cluster/${siteId}/workloads`);
  },

  /**
   * Restart a show-affecting workload (#236). `kind` must be lowercase
   * (`deployment` | `statefulset` | `daemonset`). The deployment-service
   * route enforces the `jeket.com/show-affecting` annotation contract — non-
   * annotated workloads return 403.
   */
  restartWorkload(
    siteId: string,
    namespace: string,
    kind: 'deployment' | 'statefulset' | 'daemonset',
    name: string,
  ): Promise<{ ok: true; restartedAt: string }> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/workloads/${namespace}/${kind}/${name}/restart`,
      { method: 'POST' },
    );
  },

  /**
   * Drill-down: pods owned by a single workload (#241). Returns Current
   * (running/pending) and History (terminated within ~1h, subject to
   * K8s pod GC) buckets so the operator drawer can render two tables.
   * Read-only — no role gate.
   */
  getWorkloadPods(
    siteId: string,
    namespace: string,
    kind: 'deployment' | 'statefulset' | 'daemonset',
    name: string,
  ): Promise<{ current: WorkloadPod[]; history: WorkloadPod[] }> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/workloads/${namespace}/${kind}/${name}/pods`,
    );
  },

  /**
   * Drill-down: events scoped to a single workload (#243). Filters K8s
   * events whose `involvedObject` matches the workload itself, any of
   * its current/recent pods, or — for Deployments — any of its owned
   * ReplicaSets. Sorted newest-first. `window` defaults to 1h on the
   * server; max 24h.
   */
  getWorkloadEvents(
    siteId: string,
    namespace: string,
    kind: 'deployment' | 'statefulset' | 'daemonset',
    name: string,
    window?: '1h' | '4h' | '24h',
  ): Promise<ClusterEvent[]> {
    const qs = toQuery({ window });
    return authenticatedRequest(
      `/api/cluster/${siteId}/workloads/${namespace}/${kind}/${name}/events${qs}`,
    );
  },

  /**
   * Page on-call for this site's cluster (#237). Returns a `referenceId`
   * (event timestamp) the operator quotes to on-call. Caller is gated at
   * the BFF for `Role.EventOperator+`; deployment-service mirrors the
   * gate via `requireRoleFromHeader`. Server-side emits a Kafka event on
   * `deployment.support_request` and a co-emitted audit row.
   */
  requestSupport(siteId: string, body: { notes?: string } = {}): Promise<{ referenceId: string }> {
    return authenticatedRequest(`/api/cluster/${siteId}/support-request`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /**
   * Site-operator support bundle (#244) — JSON envelope a CSR can convert
   * to a downloadable plain-text file. Composition: workload summary +
   * pods (current + history) + tail-500 logs per pod + 1h workload-scoped
   * events. Read-only; the frontend gates the trigger to SiteOperator+.
   * Per-pod log fetches are fail-soft on the server, so the envelope is
   * always returned when the workload exists.
   */
  getSupportBundle(
    siteId: string,
    namespace: string,
    kind: 'deployment' | 'statefulset' | 'daemonset',
    name: string,
  ): Promise<{
    generatedAt: string;
    workload: Workload;
    pods: WorkloadPod[];
    podLogs: Record<string, string>;
    recentEvents: ClusterEvent[];
  }> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/workloads/${namespace}/${kind}/${name}/support-bundle`,
    );
  },

  /**
   * Workload-scoped error-pattern recommendations (#260). Returns the
   * curated runbook list of patterns that match the workload's recent
   * events / pod statuses. Read-only; visible to all authed roles.
   */
  getWorkloadRecommendations(
    siteId: string,
    namespace: string,
    kind: 'deployment' | 'statefulset' | 'daemonset',
    name: string,
  ): Promise<KnownErrorPattern[]> {
    return authenticatedRequest(
      `/api/cluster/${siteId}/workloads/${namespace}/${kind}/${name}/recommendations`,
    );
  },

  // ── Secrets (#251) ──────────────────────────────────────────────
  // All three are Engineer-gated at the BFF; the per-secret reveal
  // (`getSecret`) emits a `deployment.audit` event server-side on every
  // successful call. The frontend re-calls `getSecret` on each Reveal
  // click so the audit trail captures the operator's act of looking.
  /** List secrets in a namespace (or cluster-wide). Metadata only — no values. */
  listSecrets(siteId: string, params?: { namespace?: string; type?: string }): Promise<SecretMetadata[]> {
    const qs = toQuery({
      namespace: params?.namespace,
      type: params?.type,
    });
    return authenticatedRequest(`/api/cluster/${siteId}/secrets${qs}`);
  },

  /** Read a single Secret with values base64-decoded. Audited server-side. */
  getSecret(siteId: string, namespace: string, name: string): Promise<SecretDetail> {
    return authenticatedRequest(`/api/cluster/${siteId}/secrets/${namespace}/${name}`);
  },

  /**
   * Rotate a Secret using a known pattern. v1 supports
   * `kubectl-secret-recreate`. Returns the new values once.
   */
  rotateSecret(
    siteId: string,
    namespace: string,
    name: string,
    pattern: string,
  ): Promise<SecretRotationResult> {
    const qs = `?pattern=${encodeURIComponent(pattern)}`;
    return authenticatedRequest(
      `/api/cluster/${siteId}/secrets/${namespace}/${name}/rotate${qs}`,
      { method: 'POST' },
    );
  },
};

// ─── Secret types — kept inline since this is the only consumer (#251) ───

export interface SecretMetadata {
  name: string;
  namespace: string;
  type: string;
  keys: string[];
  createdAt: string | null;
  age: string;
}

export interface SecretDetail {
  name: string;
  namespace: string;
  type: string;
  data: Record<string, string>;
  createdAt: string | null;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
}

export interface SecretRotationResult {
  ok: true;
  pattern: string;
  keysRotated: string[];
  /** Caller should display newValues exactly once and then discard. */
  newValues: Record<string, string>;
}


// ─── Phase 4 — boot-service approval gate ───────────────────────────────

export interface PendingBootApproval {
  id: string;
  code: string;
  bootTokenId: string;
  /** Parent boot_token's site_id. Surfaced (#398) so the UI can scope
   *  the approval list per-site under Deployments. Nullable defensively;
   *  in practice the FK guarantees it's set. */
  siteId: string | null;
  clientIp: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
}

export const bootApprovalsApi = {
  listPending(slug: string): Promise<PendingBootApproval[]> {
    return authenticatedRequest(`/api/boot/b/${encodeURIComponent(slug)}/approvals/pending`);
  },
  approve(slug: string, code: string): Promise<{ id: string; code: string; approvedAt: string; approvedBy: string }> {
    return authenticatedRequest(`/api/boot/b/${encodeURIComponent(slug)}/approvals/${encodeURIComponent(code)}/approve`, {
      method: 'POST',
    });
  },
  deny(slug: string, code: string, reason: string): Promise<{ id: string; code: string; deniedAt: string; deniedBy: string; deniedReason: string }> {
    return authenticatedRequest(`/api/boot/b/${encodeURIComponent(slug)}/approvals/${encodeURIComponent(code)}/deny`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

// Quick slug-by-org-id lookup using the existing admin endpoint. Superuser-only.
// Kept for any caller that legitimately needs to look up an arbitrary org.
// For "give me MY org" use `meApi.organization()` below — that endpoint is
// auth-only, not admin-gated, so it works for regular org users too.
export const orgsApi = {
  getById(orgId: string): Promise<{ id: string; slug: string; name: string }> {
    return authenticatedRequest(`/api/admin/organizations/${encodeURIComponent(orgId)}`);
  },
};

// Self-introspection for the logged-in user. The endpoint is gated by
// `authenticate` only (any signed-in user), so the Network Boot card,
// AddNodeWizard, and PendingBoots can resolve their own org slug
// without requiring superuser/admin role.
export const meApi = {
  organization(): Promise<{ id: string; slug: string; name: string }> {
    return authenticatedRequest('/api/me/organization');
  },

  // The orgs the caller may act in — their own, or every org for a
  // superuser. Feeds the org switcher (#847: /api/admin is superuser-only).
  organizations(): Promise<MyOrganization[]> {
    return authenticatedRequest('/api/me/organizations');
  },

  // The acting org's surfaces (#1160): staff-managed, every one on unless
  // staff turned it off. Read through useEntitlements().
  entitlements(): Promise<OrgEntitlements> {
    return authenticatedRequest('/api/me/entitlements');
  },
};

// Feature flags (#954), evaluated by the BFF for the acting org and, when the
// page is about one site, that site. Values only. Read through useFeature().
export const featuresApi = {
  get(siteId?: string): Promise<FeatureFlagValues> {
    const qs = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
    return authenticatedRequest(`/api/features${qs}`);
  },
};

// ─── Phase 5a — boot-service token minting (Add Node wizard) ────────────

export interface BootTokenCreateRequest {
  siteId: string;
  ttlSeconds?: number;
  allowedMac?: string;
  allowedUuid?: string;
  requireApproval?: boolean;
}

export interface BootTokenCreated {
  id: string;
  bootUrl: string;
  expiresAt: string;
}

export const bootTokensApi = {
  create(slug: string, body: BootTokenCreateRequest): Promise<BootTokenCreated> {
    return authenticatedRequest(`/api/boot/b/${encodeURIComponent(slug)}/tokens`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

// ─── Phase 5d — device-flow pairing ─────────────────────────────────────

export interface PendingPairingLookup {
  id: string;
  code: string;
  mac: string;
  uuid: string | null;
  clientIp: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
  isPaired: boolean;
  pairedTokenId: string | null;
  pairedAt: string | null;
  pairedBy: string | null;
}

export interface PairingAttachRequest {
  siteId: string;
  ttlSeconds?: number;
  requireApproval?: boolean;
}

export const pairingsApi = {
  getByCode(slug: string, code: string): Promise<PendingPairingLookup> {
    return authenticatedRequest(
      `/api/boot/b/${encodeURIComponent(slug)}/pairings/${encodeURIComponent(code)}`,
    );
  },
  attach(slug: string, code: string, body: PairingAttachRequest): Promise<BootTokenCreated> {
    return authenticatedRequest(
      `/api/boot/b/${encodeURIComponent(slug)}/pairings/${encodeURIComponent(code)}/attach`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },
};

// ---- Processing API (ADR-JSS-0006; processing-contracts.md §2.2) ----

function toProcessingQuery(params?: Record<string, string | number | undefined>): string {
  if (!params) return '';
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(params)) {
    out[k] = v == null || v === '' ? undefined : String(v);
  }
  return toQuery(out);
}

export const processingApi = {
  listRecordings(params?: { eventId?: string }): Promise<{ recordings: Recording[] }> {
    return authenticatedRequest(`/api/processing/recordings${toProcessingQuery(params)}`);
  },

  getRecording(sourceId: string): Promise<RecordingDetail> {
    return authenticatedRequest(`/api/processing/recordings/${encodeURIComponent(sourceId)}`);
  },

  listJobs(params?: JobListParams): Promise<JobList> {
    return authenticatedRequest(`/api/processing/jobs${toProcessingQuery({ ...params })}`);
  },

  getJob(id: string): Promise<ProcessingJob> {
    return authenticatedRequest(`/api/processing/jobs/${id}`);
  },

  createJob(data: CreateJobRequest): Promise<ProcessingJob> {
    return authenticatedRequest('/api/processing/jobs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  cancelJob(id: string): Promise<ProcessingJob> {
    return authenticatedRequest(`/api/processing/jobs/${id}/cancel`, { method: 'POST' });
  },

  restartJob(id: string): Promise<ProcessingJob> {
    return authenticatedRequest(`/api/processing/jobs/${id}/restart`, { method: 'POST' });
  },

  deleteJob(id: string): Promise<void> {
    return authenticatedRequest(`/api/processing/jobs/${id}`, { method: 'DELETE' });
  },

  listResults(params: { streamId: string; kind?: ResultKind }): Promise<{ results: ProcessingResult[] }> {
    return authenticatedRequest(`/api/processing/results${toProcessingQuery(params)}`);
  },

  listClips(params?: { streamId?: string }): Promise<{ clips: Clip[] }> {
    return authenticatedRequest(`/api/processing/clips${toProcessingQuery(params)}`);
  },

  approveClip(jobId: string, data: ApproveClipRequest): Promise<ApproveClipResponse> {
    return authenticatedRequest(`/api/processing/clips/${jobId}/approve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  rejectClip(jobId: string): Promise<void> {
    return authenticatedRequest(`/api/processing/clips/${jobId}/reject`, { method: 'POST' });
  },

  getSettings(): Promise<ProcessingSettings> {
    return authenticatedRequest('/api/processing/settings');
  },

  updateSettings(data: ProcessingSettings): Promise<ProcessingSettings> {
    return authenticatedRequest('/api/processing/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ---- TAMS API (read-only through the BFF; processing-contracts.md §1.1) ----

export const tamsApi = {
  listFlows(params: { sourceId: string; kind?: string }): Promise<TamsFlow[]> {
    const qs = new URLSearchParams({ source_id: params.sourceId });
    if (params.kind) qs.set('tag.jeket.kind', params.kind);
    return authenticatedRequest(`/api/tams/flows?${qs.toString()}`);
  },

  /** Segments with presigned GET URLs. One page; `limit` bounds it. */
  listSegments(flowId: string, params?: { timerange?: string; limit?: number }): Promise<TamsSegment[]> {
    const qs = new URLSearchParams({ presigned: 'true' });
    if (params?.timerange) qs.set('timerange', params.timerange);
    if (params?.limit) qs.set('limit', String(params.limit));
    return authenticatedRequest(`/api/tams/flows/${encodeURIComponent(flowId)}/segments?${qs.toString()}`);
  },
};
